import { useCallback, useState } from "react";
import type { GreenfieldClientHubProps } from "../types.js";

type DeviceFrame = "desktop" | "mobile";

export function GreenfieldPreviewView({
  active,
  shell,
  preview,
  api,
  linkId,
  sessionToken,
  approvalBaselineVersionId,
  onPreviewChange,
}: Pick<GreenfieldClientHubProps, "shell" | "preview" | "api" | "sessionToken"> & {
  readonly active: boolean;
  readonly linkId: string;
  readonly approvalBaselineVersionId?: string | null;
  readonly onPreviewChange?: (preview: NonNullable<GreenfieldClientHubProps["preview"]>) => void;
}) {
  const displayName = preview?.displayName ?? shell.project.displayName;
  const routes = preview?.availableRoutes ?? (preview?.previewRoute ? [preview.previewRoute] : ["/"]);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [device, setDevice] = useState<DeviceFrame>("desktop");
  const previewStale =
    approvalBaselineVersionId != null &&
    preview?.previewVersionId != null &&
    preview.previewVersionId !== approvalBaselineVersionId;

  const handleRouteChange = useCallback(
    async (route: string) => {
      if (!preview || route === preview.previewRoute) return;
      setLoadingRoute(true);
      try {
        const next = await api.getPreview(linkId, sessionToken, route);
        onPreviewChange?.(next);
      } finally {
        setLoadingRoute(false);
      }
    },
    [api, linkId, onPreviewChange, preview, sessionToken],
  );

  if (!active) return null;

  return (
    <section className="ws-card" id="gf-tab-preview" role="tabpanel" aria-labelledby="gf-tab-btn-preview">
      <h2 className="ws-title">Exact website preview</h2>
      {preview?.previewVersionId ? (
        <p className="ws-muted" data-testid="preview-version-id">
          Exact preview version {preview.previewVersionId}
        </p>
      ) : null}
      {previewStale ? (
        <p className="ws-global-notice" role="alert" data-testid="preview-stale-banner">
          This preview changed since you opened review. Open the current version before approving.
        </p>
      ) : null}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <button
          type="button"
          className={device === "desktop" ? "ws-btn ws-btn-primary" : "ws-btn"}
          data-testid="preview-device-desktop"
          aria-pressed={device === "desktop"}
          onClick={() => setDevice("desktop")}
        >
          Desktop
        </button>
        <button
          type="button"
          className={device === "mobile" ? "ws-btn ws-btn-primary" : "ws-btn"}
          data-testid="preview-device-mobile"
          aria-pressed={device === "mobile"}
          onClick={() => setDevice("mobile")}
        >
          Phone
        </button>
      </div>
      {routes.length > 1 ? (
        <div role="tablist" aria-label="Preview pages" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {routes.map((route) => (
            <button
              key={route}
              type="button"
              className="ws-btn"
              disabled={loadingRoute}
              onClick={() => void handleRouteChange(route)}
            >
              {route === "/" ? "Home" : route.replace(/^\//, "").replace(/-/g, " ")}
            </button>
          ))}
        </div>
      ) : null}
      <div
        data-testid="preview-device-frame"
        data-device={device}
        style={{
          marginTop: 16,
          marginInline: "auto",
          width: device === "mobile" ? 390 : "100%",
          maxWidth: "100%",
          border: "1px solid var(--ws-border-subtle)",
          borderRadius: 12,
          overflow: "auto",
          background: "var(--ws-surface-sunken)",
        }}
      >
        {preview?.previewHtml ? (
          <div aria-label="Website preview" dangerouslySetInnerHTML={{ __html: preview.previewHtml }} />
        ) : (
          <div style={{ padding: 24 }}>
            <h3 className="ws-title">{displayName}</h3>
            <p className="ws-muted">Preview appears here when ready. Nothing is edited from this screen.</p>
          </div>
        )}
      </div>
    </section>
  );
}
